/**
 * Left-to-right cascading dimension filter for the impact activity form.
 *
 * Given the dimension columns of a factor table (in display order), every
 * factor's `dimension_values`, and the current selection, this computes — per
 * column — the values that are still valid (narrowed by the selections to its
 * LEFT), scopes the selection to this table's columns (a key that isn't a column
 * of this table — e.g. a leftover pick from a previously-selected table — is
 * dropped), prunes selections that are no longer valid, auto-applies dimensions
 * with a single valid value, and decides which dimensions to reveal right now
 * (progressive disclosure: a dimension that still needs a choice is shown only
 * once every choice before it has been made).
 *
 * Pure and side-effect free so it can be unit-tested and memoized.
 */

export interface CascadeColumn {
  /** Stable id (unused here but kept so callers can pass their column rows). */
  id?: string;
  column_code: string;
  /** 'select' columns filter the cascade; others are free-input and don't. */
  column_type: string;
}

/**
 * Reserved value_code meaning "this factor explicitly has no value for this
 * dimension" — a real, persisted, matchable value, NOT a clear-to-null
 * sentinel (contrast with the `'__none__'` used elsewhere in this codebase
 * for optional FK selects, e.g. FundFormDialog, where picking it clears the
 * field). Lets a sparse table distinguish "row A has no vehicle_type" from
 * "row B has vehicle_type=electric" as two independently selectable factor
 * rows, instead of the cascade silently collapsing to whichever one has a
 * concrete value. Only ever offered on optional (non is_required) select
 * columns — see CarbonImpactPicker.tsx / EmissionFactorDialog.tsx.
 */
export const DIMENSION_NONE_VALUE = '__none__';

/** Shared label fallback for anywhere a value_code resolves to a display
 * label — keeps the "(None)" copy consistent across every render site.
 * `noneLabel` lets a caller with i18n access (e.g. a component already using
 * `t()`) supply a translated label; callers without one (pure utils, files
 * with no established `t()` usage) fall back to the plain English default. */
export function resolveDimensionValueLabel(
  valueCode: string,
  realLabel: string | undefined,
  noneLabel = '(None)',
): string {
  if (valueCode === DIMENSION_NONE_VALUE) return noneLabel;
  return realLabel ?? valueCode;
}

/**
 * Prepends a synthetic "(None)" entry to a column's real selectable values,
 * for optional (non `is_required`) select columns only. Shared by every
 * dimension-value picker (CarbonImpactPicker, AccountCarbonSettings,
 * EmissionFactorDialog) so the None-offering rule and its shape live in one
 * place instead of being re-derived per call site.
 *
 * Guards against a real column value that happens to already have
 * `value_code === DIMENSION_NONE_VALUE` (an admin can type this directly in
 * ColumnDialog) — in that case the real entry is kept as-is and no
 * second, colliding synthetic entry is added, since two options sharing the
 * same value/key have undefined Select/Combobox behavior.
 */
export function withNoneOption<T>(
  realValues: T[],
  isRequired: boolean | undefined,
  makeNoneOption: () => T,
  getValueCode: (option: T) => string,
): T[] {
  if (isRequired) return realValues;
  if (realValues.some((v) => getValueCode(v) === DIMENSION_NONE_VALUE)) return realValues;
  return [makeNoneOption(), ...realValues];
}

/**
 * Whether ANY row in the table (unnarrowed by the current selection — unlike
 * `optionsByCode`, which only reflects rows still matching selections to the
 * left) carries DIMENSION_NONE_VALUE for the given column. A pin/filter UI
 * that offers "(None)" as a value to match AGAINST existing data (as opposed
 * to a value being newly WRITTEN, like the admin factor-entry form) must
 * gate on this — otherwise it can offer a value no factor row will ever
 * match, silently making that selection permanently unresolvable.
 */
export function tableEverUsesNoneValue(
  columnCode: string,
  factorDimRows: Record<string, unknown>[] | undefined,
): boolean {
  return (factorDimRows ?? []).some((r) => r[columnCode] === DIMENSION_NONE_VALUE);
}

export interface DimensionCascade {
  /** Valid value_codes per select column, given the selections to its left. */
  optionsByCode: Record<string, Set<string>>;
  /** Selection with invalid picks dropped and single-valid dimensions applied. */
  cleanedDims: Record<string, string>;
  /** Column codes to render now (real choices not gated behind a pending one). */
  visibleCodes: Set<string>;
  /**
   * Column codes populated by at least one row still matching the selections to
   * their left. A column can be `is_required` at the table level while being
   * legitimately absent from a given factor's own dimension_values — e.g. a
   * table shared across categories where only some rows carry a given
   * dimension. Callers should gate "required fields filled" checks on this set
   * rather than on the full required-column list, so a required-but-sparse
   * dimension that no candidate row populates doesn't permanently block a
   * factor it never applied to in the first place.
   */
  applicableCodes: Set<string>;
  /**
   * Select column codes whose valid options (given the selections to their
   * left) include DIMENSION_NONE_VALUE alongside at least one other value,
   * and are not yet resolved one way or the other. A caller must block
   * readiness/persistence while this set is non-empty — otherwise the
   * Value-vs-None ambiguity can go unresolved and a downstream lookup (e.g.
   * lookup_emission_factor's containment match) silently picks one for the
   * user via its own tie-break, defeating the point of offering the choice.
   */
  hasUnresolvedNoneChoice: boolean;
}

export function computeDimensionCascade(
  columns: CascadeColumn[] | undefined,
  factorDimRows: Record<string, unknown>[] | undefined,
  dimensionValues: Record<string, string>,
): DimensionCascade {
  const optionsByCode: Record<string, Set<string>> = {};
  // Scope the incoming selection to THIS table's columns: a key for a column that
  // isn't part of this table — e.g. a leftover pick from a previously-selected table —
  // is dropped rather than copied through, so `cleanedDims` only ever contains the
  // current table's dimensions. Applied only when the columns are known; an
  // absent/empty column list means "not loaded yet" (the caller decides how to treat
  // a pending selection), so nothing is scoped then.
  const columnCodes = new Set((columns ?? []).map((c) => c.column_code));
  const cleaned: Record<string, string> =
    columnCodes.size === 0
      ? { ...dimensionValues }
      : Object.fromEntries(
          Object.entries(dimensionValues).filter(([code]) => columnCodes.has(code)),
        );
  const visible = new Set<string>();
  const applicable = new Set<string>();
  let hasUnresolvedNoneChoice = false;
  let rows = factorDimRows ?? [];
  // Once we reveal a dimension that still needs a choice, stop revealing the
  // ones after it until that choice is made.
  let blocked = false;

  for (const col of columns ?? []) {
    const code = col.column_code;

    // Free-input (non-select) dims don't filter rows; reveal in turn and gate
    // the rest until filled.
    if (col.column_type !== 'select') {
      if (rows.some((r) => r[code] != null && r[code] !== '')) applicable.add(code);
      if (!blocked) {
        visible.add(code);
        if (!cleaned[code]) blocked = true;
      }
      continue;
    }

    const valid = new Set<string>();
    for (const r of rows) {
      const v = r[code];
      if (v != null && v !== '') valid.add(String(v));
    }
    optionsByCode[code] = valid;
    if (valid.size > 0) applicable.add(code);

    // Drop a selection that's no longer valid given the left selections.
    if (cleaned[code] != null && !valid.has(cleaned[code])) delete cleaned[code];
    // Auto-apply when only one value is valid (no real choice) — stays hidden
    // and doesn't gate the cascade.
    if (valid.size === 1 && cleaned[code] == null) cleaned[code] = [...valid][0];
    // A dimension with ≥2 valid values is a real choice: reveal it only if no
    // earlier choice is still pending, and block what follows until it's set.
    if (valid.size >= 2 && !blocked) {
      visible.add(code);
      if (cleaned[code] == null) blocked = true;
    }
    // A real (unresolved) Value-vs-None ambiguity: DIMENSION_NONE_VALUE is one
    // of ≥2 valid options and nothing has been picked yet — see the
    // hasUnresolvedNoneChoice doc comment.
    if (valid.has(DIMENSION_NONE_VALUE) && cleaned[code] == null) hasUnresolvedNoneChoice = true;
    // Narrow the candidate rows by this (possibly auto-applied) selection.
    if (cleaned[code] != null) rows = rows.filter((r) => String(r[code]) === cleaned[code]);
  }

  return {
    optionsByCode,
    cleanedDims: cleaned,
    visibleCodes: visible,
    applicableCodes: applicable,
    hasUnresolvedNoneChoice,
  };
}
